import { Layer, Stage } from "react-konva";
import type { BoardScene } from "@/lib/canvas/board-schema";
import { PITCH_H, PITCH_W } from "@/lib/canvas/coords";
import { PitchBackground } from "./components/PitchBackground";
import { ShapeRenderer } from "./components/ShapeRenderer";
import { TokenRenderer } from "./components/TokenRenderer";

/**
 * Read-only tactical-board renderer.
 *
 * Sizes the Konva stage by the `width` prop, deriving height from
 * the fixed pitch aspect ratio (`PITCH_W / PITCH_H` from coords.ts)
 * so the canvas stays a faithful pitch shape at any container width.
 *
 * Used by:
 *   - Viewer surfaces (My Feedback, Player Development) where a board
 *     attached to a note renders inline as a preview.
 *   - The editor (`TacticalBoardEditor`) which wraps this and layers
 *     event handlers + the Konva.Transformer on top.
 *
 * Privacy invariant: the renderer must tolerate corrupted input
 * gracefully. The caller is expected to have run the scene through
 * `validateScene` already — but if a scene with malformed tokens or
 * shapes sneaks through, react-konva will silently no-op the
 * unrenderable nodes rather than crash.
 */

export type TacticalBoardViewProps = {
  scene: BoardScene;
  /** Render width in pixels. Height derives from the pitch aspect. */
  width: number;
  /** Optional override for the pitch fill color. */
  pitchColor?: string;
  /** When true, the Konva stage emits no events (pure decoration —
   * useful for screenshot capture and preview tiles). Defaults to
   * `false` so the editor's onClick handlers work. */
  passThrough?: boolean;
};

export function TacticalBoardView(props: TacticalBoardViewProps) {
  const aspect = PITCH_H / PITCH_W;
  const width = Math.max(1, Math.round(props.width));
  const height = Math.max(1, Math.round(width * aspect));
  const dims = { width, height };

  return (
    <Stage
      width={width}
      height={height}
      listening={!props.passThrough}
      data-testid="tactical-board-stage"
    >
      <Layer listening={false}>
        <PitchBackground dims={dims} color={props.pitchColor} />
      </Layer>
      <Layer>
        {props.scene.shapes.map((shape, i) => (
          <ShapeRenderer
            key={shape.id ?? `shape-${i}`}
            shape={shape}
            dims={dims}
          />
        ))}
        {props.scene.tokens.map((token, i) => (
          <TokenRenderer
            key={token.id ?? `token-${i}`}
            token={token}
            dims={dims}
          />
        ))}
      </Layer>
    </Stage>
  );
}
